import { ChangeDetectionStrategy, Component, computed, inject, input, output, signal } from '@angular/core';
import { MatTooltip } from '@angular/material/tooltip';

import { ApmTranslatePipe, DomainDataFacade } from '@mitsubishi/core';
import { NewsUnreadCounterComponent } from '@mitsubishi/news/shared';
import { ProjectFacade, Selection } from '@mitsubishi/project/data-access';
import { CurrencyUtils, PRICE_TYPE_LABEL, PriceTypeEnum } from '@mitsubishi/shared/data-access';
import { ApmCurrencyPipe, BadgeComponent, IconComponent, MomentDatePipe } from '@mitsubishi/shared/ui';
import { PriceComponent, PricingModule, PricingUtilsService } from '@mitsubishi/ui/pricing';

import { isNil } from '@angular-ru/cdk/utils';

import { StatisticsDataIconsComponent } from '../statistics-data/statistics-data-icons/statistics-data-icons.component';

@Component({
  selector: 'apm-selection-card',
  templateUrl: './selection-card.component.html',
  styleUrls: ['./selection-card.component.scss'],
  providers: [ApmCurrencyPipe],
  imports: [
    PricingModule,
    MomentDatePipe,
    StatisticsDataIconsComponent,
    NewsUnreadCounterComponent,
    PriceComponent,
    BadgeComponent,
    ApmTranslatePipe,
    MatTooltip,
    IconComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SelectionCardComponent {
  readonly selection = input.required<Selection>();
  readonly projectCode = input.required<string>();

  cardClick = output<MouseEvent>();

  #projectFacade = inject(ProjectFacade);
  #domainDataFacade = inject(DomainDataFacade);
  pricingUtilsService = inject(PricingUtilsService);

  readonly selectedPrice = signal<PriceTypeEnum | undefined>(this.pricingUtilsService.getDefaultPrice());
  readonly currencySymbol = computed(() => {
    const sel = this.selection();
    return CurrencyUtils.getCurrencySymbolFromCode(sel?.details?.currencyCode ?? sel?.currencyCode);
  });

  PRICE_TYPE_LABEL = PRICE_TYPE_LABEL;
  sellIn = PriceTypeEnum.sellIn;

  readonly selectionDetails = computed(() => this.selection().details);
  readonly statusResourceId = computed(
    () => this.#domainDataFacade.selectionStatusResourceMap()[this.selectionDetails().status]
  );
  readonly isCRM = computed(() => !isNil(this.selectionDetails().linkedExternalCode));
  readonly selectionCode = computed(() => `${this.projectCode()}-${this.selectionDetails().code}`);
  readonly selectionAlternativesCount = computed(() => Object.values(this.selection().alternatives).length);
  readonly hasPrices = computed(() => {
    const selectionDetails = this.selectionDetails();
    if (!selectionDetails.total) {
      return false;
    }

    const selectedPrice = this.selectedPrice();
    if (isNil(selectedPrice)) {
      return false;
    }
    const selectionTotalPrices = selectionDetails.total[selectedPrice];
    if (isNil(selectionTotalPrices)) {
      return false;
    }

    return selectionTotalPrices >= 0;
  });

  readonly unreadNewsCount = computed(() => {
    const unreadNews = Object.values(this.selection().units).reduce((acc, unit) => {
      return { ...acc, ...unit.unreadNews };
    }, {});

    return Object.values(unreadNews).length;
  });

  readonly isExpiring = computed(() => {
    const selectionDetails = this.selectionDetails();
    const offerDetails = this.selection().offer;

    return (
      (selectionDetails.expiringDays !== null &&
        selectionDetails.expiringDays >= 0 &&
        selectionDetails.expiringDays <= 14) ||
      (offerDetails.expiringDays !== null && offerDetails.expiringDays >= 0 && offerDetails.expiringDays <= 14)
    );
  });

  onCardClick($event: MouseEvent) {
    this.cardClick.emit($event);
  }

  priceChanged(event: PriceTypeEnum) {
    this.selectedPrice.set(event);
  }

  async copyCode(event: MouseEvent) {
    event.stopPropagation();
    try {
      await navigator.clipboard.writeText(this.selectionCode());
    } catch (err) {
      console.error(err);
    }
  }
}
